import { useT } from "@transifex/react";
import type { Moment } from "moment";
import PhaseSixIcon from "../phaseSixIcon/PhaseSixIcon";
import { ACTIVITY_VISIBILITY_ICONS, ActivityVisibility } from "../../helpers/ActivityVisibility";
import useBreakpoints from "../../hooks/useBreakpoints";

interface ActivityVisibilityBadgeProps {
    visibility: ActivityVisibility;
    isEditable: boolean;
    plannedDate: Moment | null;
    onEdit?: () => void;
}

const PLANNED_DATE_FORMAT = "DD.MM.YYYY - HH:mm";

const ActivityVisibilityBadge = (props: ActivityVisibilityBadgeProps) => {
    const t = useT();
    const { isAboveMD } = useBreakpoints();

    const tags = { _tags: "ClassActivity, ActivityVisibilityBadge" };
    const t_hidden = t("Hidden", tags);
    const t_planned = t("Planned", tags);
    const t_visible = t("Visible", tags);
    const t_hiddenForClass = t("Hidden for your class", tags);
    const t_visibleForClass = t("Visible for your class", tags);

    const labels: Record<ActivityVisibility, string> = {
        [ActivityVisibility.HIDDEN]: t_hidden,
        [ActivityVisibility.PLANNED]: t_planned,
        [ActivityVisibility.VISIBLE]: t_visible,
    };

    const iconName = ACTIVITY_VISIBILITY_ICONS[props.visibility];

    const renderEditableLabel = () => {
        if (props.visibility === ActivityVisibility.HIDDEN) {
            return <span className="font-semibold">{t_hiddenForClass}</span>;
        }
        if (props.visibility === ActivityVisibility.VISIBLE) {
            return <span className="font-semibold">{t_visibleForClass}</span>;
        }
        const formatted = props.plannedDate?.isValid() ? props.plannedDate.format(PLANNED_DATE_FORMAT) : null;
        return (
            <span>
                <span className="font-semibold">{t_visible}</span>
                {formatted && <span> {t("from {date} clock", { ...tags, date: formatted })}</span>}
            </span>
        );
    };

    if (!props.isEditable) {
        return (
            <span
                className="inline-flex items-center gap-xs text-p6-grey-mid text-l"
                data-testid="activity-visibility-badge"
                data-visibility={props.visibility}
            >
                <PhaseSixIcon
                    name={iconName}
                    className="text-header-icon"
                />
                <span>{labels[props.visibility]}</span>
            </span>
        );
    }

    if (isAboveMD) {
        return (
            <button
                type="button"
                data-testid="activity-visibility-badge-edit"
                data-visibility={props.visibility}
                onClick={props.onEdit}
                className="flex items-center justify-between gap-m rounded-medium border border-p6-grey-light bg-white px-s py-xs text-p6-grey-dark text-l cursor-pointer hover:bg-p6-grey-lightest w-auto"
            >
                <span className="inline-flex items-center gap-xs">
                    <PhaseSixIcon
                        name={iconName}
                        className="text-header-icon"
                    />
                    {renderEditableLabel()}
                </span>
                <PhaseSixIcon
                    name="edit-avatar"
                    className="text-header-icon"
                />
            </button>
        );
    }

    return (
        <button
            type="button"
            data-testid="activity-visibility-badge-edit"
            data-visibility={props.visibility}
            onClick={props.onEdit}
            className="flex flex-col items-stretch gap-xs rounded-medium border border-p6-grey-light bg-white px-s py-xs text-p6-grey-dark text-l cursor-pointer hover:bg-p6-grey-lightest w-full"
        >
            <span className="flex items-center gap-xs">
                <PhaseSixIcon
                    name={iconName}
                    className="text-header-icon shrink-0"
                />
                {renderEditableLabel()}
            </span>
            <PhaseSixIcon
                name="edit-avatar"
                className="text-header-icon self-end"
            />
        </button>
    );
};

export default ActivityVisibilityBadge;
